import * as React from 'react';
import {
	AccountPreferences,
	Api,
	isSymbolicLink,
	Item as ShareFileItem,
	ItemsEntity,
} from '@citrite/sf-api';
import { fileFolderSort } from '@citrite/sf-sorting';
import {
	Button,
	EmptyFolderIcon as NeutralEmptyFolderIcon,
	EmptyState,
	withModals,
	WithModalsProps,
} from '@sharefiledev/flow-web';
import {
	StyledBackIcon,
	StyledCurrentItem,
	StyledCurrentItemName,
	StyledEmptyList,
	StyledItemList,
	StyledItemsContainer,
} from './FileListModal.styles';
import { Item } from './Item';
import { NavigateAwayDialog } from './navigateAwayDialog';
import { RadioItem } from './RadioItem';

const { sortDirections } = Api;

export interface FileListModalProps extends WithModalsProps {
	disableItems: (item: ShareFileItem) => boolean;
	accountPreferences: AccountPreferences;
	initialItems: ShareFileItem[];
	checkedItems: ShareFileItem[];
	checkItems(itemIds: ShareFileItem[]): void;
	emptyStateTitle: string;
	isMultiselect?: boolean;
}

export interface State {
	breadcrumbs?: ShareFileItem[];
	currentFolder?: ShareFileItem;
	items?: ShareFileItem[];
	isRootLevelParentSymbolicLink: boolean;
}

export const storageKey = 'file-list.confirm';

class _FileListModal extends React.Component<FileListModalProps, State> {
	public state: State = this.getDefaultState();

	private getDefaultState(): State {
		return {
			breadcrumbs: [],
			currentFolder: null,
			items: this.props.initialItems,
			isRootLevelParentSymbolicLink: false,
		};
	}

	public render() {
		return (
			<StyledItemsContainer>
				{this.state.currentFolder && (
					<Button.Wrapper onClick={this.navigateUp}>
						<StyledCurrentItem>
							<StyledBackIcon />
							<StyledCurrentItemName>
								{this.state.currentFolder.Name}
							</StyledCurrentItemName>
						</StyledCurrentItem>
					</Button.Wrapper>
				)}
				{!this.state.items.length ? (
					<StyledEmptyList>
						<EmptyState
							icon={NeutralEmptyFolderIcon}
							title={this.props.emptyStateTitle}
						/>
					</StyledEmptyList>
				) : (
					<StyledItemList role="grid" aria-multiselectable>
						{this.state.items.map(item => {
							return !this.props.isMultiselect ? (
								<RadioItem
									key={item.Id}
									accountPreferences={this.props.accountPreferences}
									disabled={this.props.disableItems(item)}
									initialItems={this.props.initialItems}
									isChecked={
										!this.props.disableItems(item) &&
										this.props.checkedItems.some(c => c.Id === item.Id)
									}
									onChecked={this.onChecked}
									onClick={this.navigate}
									item={item}
									isRootLevelParentSymbolicLink={this.state.isRootLevelParentSymbolicLink}
								/>
							) : (
								<Item
									key={item.Id}
									accountPreferences={this.props.accountPreferences}
									disabled={this.props.disableItems(item)}
									initialItems={this.props.initialItems}
									isChecked={
										!this.props.disableItems(item) &&
										this.props.checkedItems.some(c => c.Id === item.Id)
									}
									onChecked={this.onChecked}
									onClick={this.navigate}
									item={item}
									isRootLevelParentSymbolicLink={this.state.isRootLevelParentSymbolicLink}
								/>
							);
						})}
					</StyledItemList>
				)}
			</StyledItemsContainer>
		);
	}

	private onChecked = (item: ShareFileItem) => {
		if (!this.props.isMultiselect) {
			this.props.checkItems([item]);
		} else {
			const itemIndex = this.props.checkedItems.findIndex(c => c.Id === item.Id);
			const checkedItems =
				itemIndex === -1
					? [...this.props.checkedItems, item]
					: [
							...this.props.checkedItems.slice(0, itemIndex),
							...this.props.checkedItems.slice(itemIndex + 1),
					  ];
			this.props.checkItems(checkedItems);
		}
	};

	private selectedItems() {
		const checkedItems = this.props.checkedItems.filter(
			item => !this.props.disableItems(item)
		);
		return this.state.items.filter(item => checkedItems.some(c => c.Id === item.Id));
	}

	private getChildren(parent: ShareFileItem) {
		const parentUrlOrId = parent.url || parent.Id;
		return ItemsEntity.getChildren(parentUrlOrId, false)
			.expand('Bundle')
			.expand('Parent')
			.expand('Zone')
			.select('Id')
			.select('Name')
			.select('StreamID')
			.select('FileName')
			.select('FileSizeBytes')
			.select('Bundle')
			.select('Bundle/*')
			.select('Parent')
			.select('Parent/Id')
			.select('Parent/url')
			.select('ExpirationDate')
			.select('VirusStatus')
			.select('Uri')
			.select('Zone')
			.select('Zone/Id')
			.select('url')
			.sortBy('Name')
			.sortDirection(sortDirections.ASCENDING)
			.execute()
			.then(result => {
				return fileFolderSort(result.value as any, {
					key: 'Name',
					direction: sortDirections.ASCENDING,
				});
			});
	}

	private canNavigate() {
		const hasSelectedItems = this.selectedItems().length > 0;
		if (!hasSelectedItems || localStorage.getItem(storageKey)) {
			return Promise.resolve();
		}

		return new Promise<boolean>((resolve, reject) => {
			this.props.showModal(
				<NavigateAwayDialog onCloseSuccess={resolve} onDismiss={reject} />
			);
		}).then(dismiss => {
			if (dismiss) {
				localStorage.setItem(storageKey, 'true');
			}
		});
	}

	private navigate = (item: ShareFileItem) => {
		return this.canNavigate()
			.then(() => {
				const isRootLevelParentSymbolicLink =
					isSymbolicLink(item) || this.state.isRootLevelParentSymbolicLink;
				this.getChildren(item).then(children => {
					this.state.breadcrumbs.push(item);
					this.updateStateFromNavigate(children, isRootLevelParentSymbolicLink);
				});
			})
			.catch(() => {});
	};

	private navigateUp = () => {
		this.canNavigate()
			.then(() => {
				if (this.state.breadcrumbs.length > 1) {
					const parent = this.state.breadcrumbs.slice(-2)[0];
					const currentFolder = this.state.breadcrumbs.slice(-2)[1];
					const isRootLevelParentSymbolicLink = isSymbolicLink(currentFolder)
						? false
						: this.state.isRootLevelParentSymbolicLink;
					this.getChildren(parent).then(children => {
						this.state.breadcrumbs.pop();
						this.updateStateFromNavigate(children, isRootLevelParentSymbolicLink);
					});
				} else {
					this.setState(this.getDefaultState());
				}
			})
			.catch(() => {});
	};

	private updateStateFromNavigate(
		children: ShareFileItem[],
		isRootLevelParentSymbolicLink: boolean
	) {
		this.setState(
			state => ({
				breadcrumbs: state.breadcrumbs,
				currentFolder: state.breadcrumbs.slice(-1)[0],
				items: children,
				isRootLevelParentSymbolicLink,
			}),
			() => {
				this.props.checkItems([]);
			}
		);
	}
}

export const FileListModal = withModals(_FileListModal);
